import { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MfaSetup, BackupCodes } from "@/components/security/MfaSetup";
import { KeyRound, Loader2 } from "lucide-react";

function CodeStep({ token, onDone }) {
  const { verifyMfa } = useAuth();
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(false);
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await verifyMfa(token, code);
    setBusy(false);
    if (!res.ok) { toast.error(res.error); setCode(""); return; }
    if (res.usedBackup) toast.message(`Backup code used. ${res.backupRemaining} left — you can make new ones in Account.`);
    onDone(res.user);
  };
  return (
    <form onSubmit={go} className="space-y-3" data-testid="mfa-code-step">
      <p className="text-sm text-muted-foreground">{backup ? "Type one of your backup codes." : "Open your authenticator app and type the 6-digit code for HireStream."}</p>
      <Input autoFocus value={code} onChange={(e) => setCode(backup ? e.target.value.slice(0, 14) : e.target.value.replace(/\D/g, "").slice(0, 6))}
        inputMode={backup ? "text" : "numeric"} autoComplete="one-time-code" placeholder={backup ? "xxxxxx-xxxxxx" : "123456"} className="h-12 text-center font-mono text-lg tracking-[0.3em]" data-testid="mfa-code-input" />
      <Button type="submit" className="w-full" disabled={busy || code.length < 6} data-testid="mfa-verify-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify and sign in"}</Button>
      <button type="button" onClick={() => { setBackup((b) => !b); setCode(""); }} className="text-xs text-primary hover:underline" data-testid="mfa-use-backup">{backup ? "Use my authenticator app" : "Use a backup code instead"}</button>
    </form>
  );
}

function SetupStep({ token, onDone }) {
  const { adoptSession } = useAuth();
  const [done, setDone] = useState(null);
  if (done) return <BackupCodes codes={done.backup_codes} onDone={() => { adoptSession(done); onDone(done.user); }} />;
  return (
    <div className="space-y-3" data-testid="mfa-required-setup">
      <p className="text-sm text-muted-foreground">Your organization requires 2-step sign-in. Set it up once now; next time you'll just type a code.</p>
      <MfaSetup mfaToken={token} onEnabled={setDone} />
    </div>
  );
}

export function MfaStep({ challenge, onDone, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm p-6" data-testid="mfa-step">
      <motion.div initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: "spring", stiffness: 240, damping: 24 }}
        className="w-full max-w-md rounded-2xl border border-border bg-card p-6 space-y-4">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center"><KeyRound className="h-5 w-5 text-primary" /></span>
          <h2 className="font-display text-xl font-semibold">2-step sign-in</h2>
        </div>
        {challenge.mfa_setup_required ? <SetupStep token={challenge.mfa_token} onDone={onDone} /> : <CodeStep token={challenge.mfa_token} onDone={onDone} />}
        <button onClick={onCancel} className="text-xs text-muted-foreground hover:text-foreground" data-testid="mfa-cancel">Back to sign in</button>
      </motion.div>
    </div>
  );
}
